// Greenhold <RevealCeremony> — the sacred moment (Epic 8 design pass).
//
// A faithful port of the design handover (docs/runbooks/RevealCeremony):
// a sealed dossier is broken open and the certificate inside is revealed.
// THE ONLY surface in the product allowed big motion ("ceremony is sacred,
// feed is calm") — this screen spends the whole motion budget.
//
// Phases: approach → breaking (0 → 0.55T) → revealing (0.55T → T) → settled.
//   * approach   — the sealed parchment gatefold floats; "Break the seal".
//   * breaking   — the gatefold splits in real 3D (each half rotates open on
//                  its outer hinge, showing a darker backside + a paper-
//                  thickness edge); tier-coloured "burst" light pours from
//                  the seam; the certificate is mounted BEHIND, holding at
//                  scale 0.7 inside the opening dossier.
//   * revealing  — halves swing fully open + fade; the certificate's z-order
//                  flips above the back panel and it zooms 0.7 → 1.0.
//   * settled    — the finished certificate rests; caption + actions.
//
// T (total) is tier-scaled: common 4.5s · preferred 6s · premium 7.5s ·
// founders 10s. Reduced-motion collapses to 0.6s (same sequence).
//
// Real-data integration (the prototype rolls client-side; we don't): the
// tier/creator/condition come from the server reveal. On "Break the seal"
// the caller's `onBreak` fires the reveal mutation; the ceremony waits for
// `share` (so timing is tier-accurate) then plays. In replay, `share` is
// already set so it plays immediately. Serial formatting stays app-side
// (passed as `serial`). The certificate itself is passed as `certificate`
// so the app owns the <PatronageCertificate> mapping.

import { useEffect, useRef, useState, type FC, type ReactNode } from "react";
import { Button } from "../ui/button";
import {
  WaxSealArt,
  deriveWaxPalette,
  type SealedDossierData,
} from "./SealedDossier";
import type { CertificateShare, CertificateTier } from "./Certificate";

// Kept for back-compat with the preference hooks (useEffectiveCeremonyMode /
// useEffectiveMotionMode) that still import this type name.
export type RevealCeremonyMode = "full" | "quick-reveal" | "reduced-motion";

// The ceremony beats, each landing a distinct emotional note:
//   approach → tearing  (suspense: the wax carefully rips)
//            → burst    (the tier reveal: tier-coloured light floods out)
//            → opening  (suspense: the parchment gatefold swings open; the cert
//                        is glimpsed behind it, BLURRED)
//            → hold     (a brief pause, pages fully open, cert still blurred)
//            → rising   (the "who" reveal: the cert zooms to full + SHARPENS)
//            → settled  (the signature inks in — if the cert rolled signed)
type Phase =
  | "approach"
  | "tearing"
  | "burst"
  | "opening"
  | "hold"
  | "rising"
  | "settled";

// ── Tier tables ──────────────────────────────────────────────────────────
const TIER_T: Record<CertificateTier, number> = {
  common: 4500,
  preferred: 6000,
  premium: 7500,
  founders: 10000,
};
const TIER_LIGHT: Record<CertificateTier, string> = {
  common: "#7bb796",
  preferred: "#5e8ec5",
  premium: "#8b5cf6",
  founders: "#e9cf85",
};
const TIER_LABEL: Record<CertificateTier, string> = {
  common: "Common",
  preferred: "Preferred",
  premium: "Premium",
  founders: "Founder's",
};

const EASE = "cubic-bezier(0.2,0,0,1)";
const REDUCED_T = 600;
// Signature pen: it flies in from screen-right to the signature start, scribbles
// up/down along the ink wipe, then flies back out right. The wipe (gh-sig-ink)
// is delayed by PEN_IN so it starts only once the pen arrives, and runs `linear`
// so the pen (linear x) tracks the wipe front exactly.
const PEN_IN = 440; // fly-in
const SIG_DRAW = 900; // ink wipe (== gh-sig-ink duration)
const PEN_OUT = 380; // fly-out
const PEN_TOTAL = PEN_IN + SIG_DRAW + PEN_OUT;
const MONO = "var(--gh-font-mono, 'JetBrains Mono', ui-monospace, monospace)";
const DOSSIER_W = 230;
const DOSSIER_H = Math.round(DOSSIER_W * 1.414); // 325
// The tear is a dark crevice in the wax, NOT a light — a touch DARKER than
// the wax surface, so the crack reads as depth. The tier colour is saved for
// the burst; the tear stays muted (suspense beat). Since 0079 the ink comes
// from `deriveWaxPalette(...).tearInk` so it tracks the portfolio's seal
// color (the default derives back to the original #54121b).

// ── Keyframes (verbatim from the handover prototype) ─────────────────────
const KEYFRAMES = `
@keyframes gh-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
@keyframes gh-half-l{0%{transform:rotateY(0deg)}8%{transform:rotateY(-2deg)}35%{transform:rotateY(-11deg)}100%{transform:rotateY(-96deg)}}
@keyframes gh-half-r{0%{transform:rotateY(0deg)}8%{transform:rotateY(2deg)}35%{transform:rotateY(11deg)}100%{transform:rotateY(96deg)}}
/* Burst = the tier reveal: fast onset, slow fade (the light blooms in a beat,
   then lingers/decays across the whole burst duration into the page-open). */
@keyframes gh-seam{0%{opacity:0;transform:scaleX(0.05)}16%{opacity:1;transform:scaleX(0.85)}30%{opacity:1;transform:scaleX(1)}100%{opacity:0;transform:scaleX(1)}}
@keyframes gh-core{0%{opacity:0;transform:scaleY(0.2)}16%{opacity:1;transform:scaleY(1)}34%{opacity:0.9}100%{opacity:0}}
@keyframes gh-rays{0%{opacity:0;transform:scale(0.4)}18%{opacity:1;transform:scale(0.95)}32%{opacity:0.95;transform:scale(1)}100%{opacity:0;transform:scale(1.14)}}
@keyframes gh-ring{0%{opacity:0;transform:scale(0.15)}14%{opacity:0.9;transform:scale(0.5)}55%{opacity:0;transform:scale(1.5)}100%{opacity:0;transform:scale(1.6)}}
@keyframes gh-spark{0%{opacity:0;transform:translateX(8px) scale(0.5)}16%{opacity:1}100%{opacity:0;transform:translateX(110px) scale(1)}}
/* Certificate: held SMALL + BLURRED behind the opening pages and through the
   brief fully-open pause, then zoomed to full and SHARPENED into focus (the
   "who" reveal). filter clears to none at the end so the settled cert composites
   exactly as it does statically (matters for the signature's multiply blend). */
@keyframes gh-cert-rise{0%{opacity:0.45;transform:scale(0.66);filter:blur(7px)}14%{opacity:0.72;transform:scale(0.7);filter:blur(7px)}62%{opacity:0.82;transform:scale(0.72);filter:blur(6.5px)}100%{opacity:1;transform:scale(1);filter:none}}
@keyframes gh-fadeup{0%{opacity:0;transform:translateY(8px)}100%{opacity:1;transform:translateY(0)}}
@keyframes gh-rc-marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
/* Signature: hidden while the cert rises, then inks in left→right (a wipe
   reveal along the writing direction) once the cert has settled. The ceremony
   toggles gh-cert-sig-hidden → gh-cert-sig-draw on the cert wrapper. */
.gh-cert-sig-hidden .gh-cert-signature{opacity:0}
.gh-cert-sig-shown .gh-cert-signature{opacity:1}
.gh-cert-sig-draw .gh-cert-signature{animation:gh-sig-ink ${SIG_DRAW}ms linear ${PEN_IN}ms both}
@keyframes gh-sig-ink{0%{opacity:0;clip-path:inset(0 100% 0 0);-webkit-clip-path:inset(0 100% 0 0)}20%{opacity:1}100%{opacity:1;clip-path:inset(0 0 0 0);-webkit-clip-path:inset(0 0 0 0)}}
/* Wax seal: the "intact" sealer retracts top→bottom, uncovering the two torn
   halves beneath — a careful tear that zips down the seal. Sides/bottom are
   inset -20% so the wax art's overspill (its melted rim) is NOT clipped; only
   the top edge travels down. */
@keyframes gh-wax-unzip{from{clip-path:inset(-20% -20% -20% -20%);-webkit-clip-path:inset(-20% -20% -20% -20%)}to{clip-path:inset(100% -20% -20% -20%);-webkit-clip-path:inset(100% -20% -20% -20%)}}
/* The crack front, revealed top→down as the tear zips, then HELD (no fade) so
   the wax stays visibly torn through the burst — it must not "heal" before the
   pages open. Top/sides start at -20% so the reveal covers the wax art's
   melted-rim overspill (the seal spills ~13% past its box). */
@keyframes gh-wax-tearlight{0%{clip-path:inset(-20% -20% 120% -20%);-webkit-clip-path:inset(-20% -20% 120% -20%)}100%{clip-path:inset(-20% -20% -20% -20%);-webkit-clip-path:inset(-20% -20% -20% -20%)}}
`;

// ── Seeded smooth-blob path (light-FX ring; same PRNG as the wax seal) ───
function wobble(
  cx: number,
  cy: number,
  rBase: number,
  variance: number,
  n: number,
  seed: number,
): string {
  let rnd = seed;
  const rand = () => (rnd = (rnd * 9301 + 49297) % 233280) / 233280;
  const pts: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const r = rBase + (rand() - 0.5) * 2 * variance;
    pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  let d = "M" + pts[0][0].toFixed(1) + " " + pts[0][1].toFixed(1);
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d +=
      "C" +
      c1[0].toFixed(1) + " " + c1[1].toFixed(1) + " " +
      c2[0].toFixed(1) + " " + c2[1].toFixed(1) + " " +
      p2[0].toFixed(1) + " " + p2[1].toFixed(1);
  }
  return d + "Z";
}

// ── "burst" light FX from the seam (the shipped style). Seeded-random so
//    no two ceremonies flash alike. Ported from the prototype's lightFX. ──
function burstFX(
  L: string,
  w: number,
  ms: number,
  seedBase: number,
  straightCore = true,
  // Story 6.6 — reduced mobile-fidelity tier. When true the burst sheds
  // its most GPU-costly work: fewer sparks, the glow blobs and the
  // blurred shockwave ring are dropped, and blur radii are capped. This
  // is the concrete fallback the auto-degrade guard switches a janky
  // session into (exactly the "oversized particles / shadow blurs" the
  // CI failure message calls out).
  lite = false,
): ReactNode[] {
  let s = ((seedBase || 11) + 7) % 233280;
  const rnd = () => (s = (s * 9301 + 49297) % 233280) / 233280;
  const blobR = () =>
    `${40 + rnd() * 25}% ${75 - rnd() * 25}% ${40 + rnd() * 25}% ${
      60 - rnd() * 20
    }% / ${45 + rnd() * 20}% ${40 + rnd() * 25}% ${55 - rnd() * 20}% ${
      50 + rnd() * 15
    }%`;
  const alphas = ["40", "4d", "59", "66", "73"];
  const els: ReactNode[] = [];

  // Ray fans ×3 — each fans at its own speed for a multi-speed shimmer.
  const fans = [0, 1, 2].map((li) => {
    let stops = "transparent 0deg";
    let edge = 0;
    const n = 2 + Math.floor(rnd() * 2);
    for (let i = 0; i < n; i++) {
      const a = edge + 3 + rnd() * 16;
      const wd = 1.5 + rnd() * 8;
      stops += `, transparent ${a.toFixed(1)}deg, ${L}${
        alphas[Math.floor(rnd() * alphas.length)]
      } ${(a + wd / 2).toFixed(1)}deg, transparent ${(a + wd).toFixed(1)}deg`;
      edge = a + wd;
    }
    return (
      <div
        key={"fan" + li}
        style={{
          position: "absolute",
          left: "50%",
          top: "-10%",
          width: w * (0.65 + rnd() * 0.55),
          height: "120%",
          transformOrigin: "0% 50%",
          background: `conic-gradient(from ${(50 + rnd() * 20).toFixed(
            0,
          )}deg at 0% 50%, ${stops}, transparent ${(edge + 2).toFixed(1)}deg)`,
          filter: `blur(${(1 + rnd() * 2.2).toFixed(1)}px)`,
          animation: `gh-rays ${Math.round(ms * (0.65 + rnd() * 0.7))}ms ${EASE} ${(
            rnd() * 130
          ).toFixed(0)}ms both`,
        }}
      />
    );
  });
  els.push(
    <div key="fans" style={{ position: "absolute", inset: 0 }}>
      {fans}
    </div>,
  );

  // White-hot core at the seam. Skipped when a jagged seam-light traces the
  // torn edge instead (so the light follows the tear, not a straight column).
  if (straightCore) {
    els.push(
      <div
        key="core"
        style={{
          position: "absolute",
          left: "50%",
          top: `${8 + rnd() * 18}%`,
          height: `${52 + rnd() * 32}%`,
          width: 0,
          transform: `rotate(${((rnd() - 0.5) * 9).toFixed(1)}deg)`,
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: `0 ${-(3 + rnd() * 4)}px`,
            borderRadius: "50%",
            background: `radial-gradient(ellipse 100% 60% at 50% 50%, rgba(255,255,255,${
              0.75 + rnd() * 0.2
            }) 0, ${L}90 45%, ${L}00 78%)`,
            filter: `blur(${(1.5 + rnd() * 2).toFixed(1)}px)`,
            animation: `gh-core ${ms}ms ${EASE} ${(rnd() * 60).toFixed(0)}ms both`,
          }}
        />
      </div>,
    );
  } else {
    // Keep the PRNG stream in step with the straight-core branch (7 draws) so
    // the rest of the burst (glows/ring/sparks) is unchanged either path.
    rnd(); rnd(); rnd(); rnd(); rnd(); rnd(); rnd();
  }

  // Glow blobs ×3 — dropped entirely in the reduced mobile-fidelity tier
  // (large, heavily-blurred layers are the costliest compositing work).
  const ring = Math.round(w * 0.66);
  for (let i = 0; !lite && i < 3; i++) {
    const gs = ring * (0.8 + rnd() * 0.8);
    const ox = (rnd() - 0.5) * 44;
    const oy = (rnd() - 0.5) * 56;
    els.push(
      <div
        key={"glow" + i}
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          width: gs,
          height: gs * (0.5 + rnd() * 0.75),
          margin: `${(-gs / 2 + oy).toFixed(0)}px 0 0 ${(-gs / 2 + ox).toFixed(
            0,
          )}px`,
          borderRadius: blobR(),
          transform: `rotate(${(rnd() * 360).toFixed(0)}deg)`,
          background: `radial-gradient(circle at ${30 + rnd() * 40}% ${
            30 + rnd() * 40
          }%, rgba(255,255,255,${0.5 + rnd() * 0.35}) 0, ${L}8c ${
            25 + rnd() * 15
          }%, ${L}00 ${62 + rnd() * 14}%)`,
          filter: `blur(${(4 + rnd() * 5).toFixed(1)}px)`,
          animation: `gh-seam ${ms}ms ${EASE} ${(rnd() * 90).toFixed(0)}ms both`,
        }}
      />,
    );
  }

  // Wobbly shockwave ring — a blurred, drop-shadowed SVG that scales
  // across the whole stage; skipped in the reduced mobile-fidelity tier.
  const rv = ring * (1.3 + rnd() * 0.25);
  if (!lite)
  els.push(
    <svg
      key="ring"
      viewBox="0 0 200 200"
      style={{
        position: "absolute",
        left: "50%",
        top: "50%",
        width: rv,
        height: rv,
        margin: `${-rv / 2}px 0 0 ${-rv / 2}px`,
        overflow: "visible",
        filter: `blur(${(0.6 + rnd()).toFixed(1)}px) drop-shadow(0 0 10px ${L}88)`,
        animation: `gh-ring ${ms}ms ${EASE} forwards`,
      }}
    >
      <path
        d={wobble(
          100,
          100,
          60 + rnd() * 14,
          10 + rnd() * 13,
          8 + Math.floor(rnd() * 6),
          Math.floor(s),
        )}
        fill="none"
        stroke={L}
        strokeWidth={Number((1.2 + rnd() * 1.8).toFixed(1))}
        strokeOpacity={0.85}
      />
    </svg>,
  );

  // Sparks ×5–9 (×2 in the reduced mobile-fidelity tier), shooting
  // radially outward.
  const nSp = lite ? 2 : 5 + Math.floor(rnd() * 5);
  for (let i = 0; i < nSp; i++) {
    const a = -85 + rnd() * 205;
    const k = 0.55 + rnd() * 0.8;
    const d = 3 + rnd() * 4;
    els.push(
      <div
        key={"sp" + i}
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: `rotate(${a.toFixed(0)}deg) scale(${k.toFixed(2)})`,
        }}
      >
        <div
          style={{
            width: d,
            height: d * (0.6 + rnd() * 0.7),
            borderRadius: blobR(),
            background: "#fff",
            boxShadow: `0 0 ${6 + rnd() * 7}px ${1 + rnd() * 3}px ${L}`,
            animation: `gh-spark ${ms}ms ${EASE} ${(rnd() * 160).toFixed(
              0,
            )}ms both`,
            opacity: 0,
          }}
        />
      </div>,
    );
  }
  return els;
}

// ── The tear that rips ONLY the wax seal (the parchment parts straight down
//    its fold). ONE fixed, hand-tuned crack (V1): random tears occasionally
//    produced bad clips (missing wax / parchment poking through), so we locked
//    a shape we're happy with. A tight lightning wander down the middle. Points
//    are [dx, fy]: dx = x-offset from centre (fraction of `s`); fy = 0→1 down
//    the FULL visible wax, including the ~13% melted-rim overspill at top and
//    bottom, so the tear starts at the very top of the wax and runs past the
//    bottom rim (no parchment poking through the ends). ─
const WAX_TEAR: [number, number][] = [
  [0.0, 0.0],
  [0.02, 0.13], // gentle curve, drifting right
  [0.032, 0.28],
  [0.018, 0.4],
  [0.03, 0.49], // ── the one sharp zig ──
  [-0.028, 0.56], // ── …and zag ──
  [-0.01, 0.67], // back to gentle curves
  [-0.03, 0.79],
  [-0.006, 0.91],
  [0.0, 1.0],
];

// Catmull-Rom → cubic-bezier through the points (open curve), the SAME smoothing
// the deckled parchment / cert edges use — so the crack curves organically
// instead of reading as straight zig-zag segments. Returns the "C…" commands
// that follow an initial "M pts[0]"; the current point must already be pts[0].
function smoothCurve(pts: [number, number][]): string {
  const f = (v: number) => v.toFixed(2);
  const n = pts.length;
  let d = "";
  for (let i = 0; i < n - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(n - 1, i + 2)];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += `C${f(c1x)} ${f(c1y)} ${f(c2x)} ${f(c2y)} ${f(p2[0])} ${f(p2[1])}`;
  }
  return d;
}

function waxTearLine(s: number): [number, number][] {
  // The crack spans the ACTUAL visible wax at the centre-line — the blob's
  // top/bottom tips sit only ~4% past its box, NOT the full clip padding (which
  // overshot well above/below the wax). Kept a hair inside the tips so the soft
  // edge of the crack can't bleed onto the parchment. x wanders around centre.
  // Measured: at the centre column the wax edge runs frac -0.038 … 0.983, so
  // the crack starts right at the wax top and finishes right at its bottom
  // (kept a hair inside each end so the soft dark halo can't spill onto the
  // parchment — the halo is also tamed in waxTearLight).
  const top = -s * 0.035;
  const span = s * 0.995; // -0.035s … 0.96s
  const cx = s / 2;
  return WAX_TEAR.map(([dx, fy]) => [cx + dx * s, top + fy * span]);
}

// clip-path polygons for the two halves of the seal, split on `pts`. The outer
// bounds extend `PAD` past the box (top/bottom too, via spine end-caps) so the
// wax art's ~13% melted-rim overspill is NOT cropped — only the jagged tear is
// cut. Coordinates are in the seal's `s×s` box.
function waxClips(pts: [number, number][], s: number) {
  const f = (n: number) => n.toFixed(1);
  const PAD = Math.round(s * 0.2);
  const lo = -PAD;
  const hi = s + PAD;
  const p0 = pts[0];
  const last = pts[pts.length - 1];
  // The spine is the SAME smooth curve the tear light traces (so the wax edges
  // follow the drawn crack when the pages separate). Outer bounds are straight;
  // both halves share the exact spine so they tile with no gap.
  const spine = smoothCurve(pts);
  const left =
    `path('M ${f(lo)} ${f(lo)} L ${f(p0[0])} ${f(lo)} L ${f(p0[0])} ${f(p0[1])} ` +
    `${spine} L ${f(last[0])} ${f(hi)} L ${f(lo)} ${f(hi)} Z')`;
  const right =
    `path('M ${f(hi)} ${f(lo)} L ${f(p0[0])} ${f(lo)} L ${f(p0[0])} ${f(p0[1])} ` +
    `${spine} L ${f(last[0])} ${f(hi)} L ${f(hi)} ${f(hi)} Z')`;
  return { left, right };
}

// The tear front — traces the crack as a DARK recess in the wax (a soft dark-
// bordeaux wash under a thin darker core), revealed top→down as the tear zips,
// then faded. Not a light: no outer glow, small blur, endpoints kept inside the
// wax tips — so the crack can't bleed onto the parchment at either end.
function waxTearLight(
  pts: [number, number][],
  L: string,
  s: number,
  ms: number,
): ReactNode {
  const d =
    `M${pts[0][0].toFixed(2)} ${pts[0][1].toFixed(2)}` + smoothCurve(pts);
  return (
    <svg
      viewBox={`0 0 ${s} ${s}`}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        overflow: "visible",
        animation: `gh-wax-tearlight ${ms}ms ${EASE} both`,
        pointerEvents: "none",
      }}
    >
      {/* Soft, low-opacity dark wash — the ambient shadow the crack casts into
          the surrounding wax (reads as depth: the wax recedes into the groove).
          Kept narrow/faint so it can't bleed past the wax at the ends. */}
      <path
        d={d}
        fill="none"
        stroke="#38070d"
        strokeWidth={4}
        strokeOpacity={0.26}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ filter: "blur(1.4px)" }}
      />
      <path
        d={d}
        fill="none"
        stroke={L}
        strokeWidth={3.5}
        strokeOpacity={0.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ filter: "blur(1.2px)" }}
      />
      <path
        d={d}
        fill="none"
        stroke={L}
        strokeWidth={1.3}
        strokeOpacity={0.9}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── The signature pen. A fountain pen drawn nib-down; its nib tip sits at the
//    sprite's local origin (0,0) so the wrapper's translate places the NIB on
//    the target point. Angled like a held pen (leans right). ──
function PenSprite(): ReactNode {
  return (
    <svg
      width="72"
      height="96"
      viewBox="0 0 72 96"
      // Offset so the nib (local 36,92) lands on the wrapper origin (0,0).
      style={{
        position: "absolute",
        left: -36,
        top: -92,
        overflow: "visible",
        filter: "drop-shadow(0 3px 4px rgba(0,0,0,0.4))",
      }}
    >
      <g transform="rotate(32 36 92)">
        {/* barrel */}
        <rect x="28" y="4" width="16" height="62" rx="7" fill="#1d1712" />
        {/* body sheen */}
        <rect
          x="30.5"
          y="9"
          width="3.6"
          height="44"
          rx="1.8"
          fill="rgba(255,255,255,0.16)"
        />
        {/* gold band */}
        <rect x="28" y="19" width="16" height="6" fill="#c99a3b" />
        {/* gold clip */}
        <rect x="42.5" y="12" width="2.6" height="22" rx="1.3" fill="#c99a3b" />
        {/* grip cone */}
        <polygon points="28,66 44,66 40,80 32,80" fill="#2a2019" />
        {/* nib */}
        <polygon points="32,80 40,80 36,93" fill="#c9c9cf" />
        <polygon points="32,80 36,80 36,93" fill="#e8e8ec" />
        <line
          x1="36"
          y1="82"
          x2="36"
          y2="92"
          stroke="#84848b"
          strokeWidth="1"
        />
      </g>
    </svg>
  );
}

// Build the pen's keyframes from the MEASURED signature rect (viewport coords —
// the ceremony overlay is fixed inset:0, so viewport == overlay space). The nib
// flies in from off-screen right, tracks the ink wipe left→right while scribbling
// up/down within the signature's height, then flies back out right.
function buildPenScribble(
  rect: { left: number; right: number; top: number; height: number },
  vw: number,
): string {
  const inEnd = (PEN_IN / PEN_TOTAL) * 100;
  const scribbleEnd = ((PEN_IN + SIG_DRAW) / PEN_TOTAL) * 100;
  const startX = vw + 70; // off the right edge
  const x0 = rect.left; // signature start (wipe origin)
  const x1 = rect.right; // signature end
  const midY = rect.top + rect.height * 0.5;
  const amp = Math.min(26, rect.height * 0.32); // scribble within the box
  // Fixed up/down pattern (negative = up); starts + ends on the baseline.
  const osc = [0, -0.9, 0.7, -1, 0.55, -0.85, 0.95, -0.6, 0.8, -0.4, 0];
  const f = (n: number) => n.toFixed(1);
  const tf = (x: number, y: number, rot: number) =>
    `translate(${f(x)}px,${f(y)}px) rotate(${f(rot)}deg)`;
  let k = "";
  k += `0%{opacity:0;transform:${tf(startX, midY, 18)}}`;
  k += `${f(inEnd * 0.16)}%{opacity:1}`;
  k += `${f(inEnd)}%{opacity:1;transform:${tf(x0, midY, 14)}}`;
  const n = osc.length;
  for (let i = 1; i < n; i++) {
    const v = i / (n - 1);
    const pct = inEnd + v * (scribbleEnd - inEnd);
    const x = x0 + v * (x1 - x0);
    const y = midY + amp * osc[i];
    k += `${f(pct)}%{transform:${tf(x, y, 14 + osc[i] * 5)}}`;
  }
  k += `90%{opacity:1}`;
  k += `100%{opacity:0;transform:${tf(startX, midY, 18)}}`;
  return `@keyframes gh-pen-scribble{${k}}`;
}

// ── The parchment gatefold face (rendered whole in `approach`, and twice
//    clipped into halves in `breaking`). Reuses the shared <WaxSealArt>. ──
function DossierFace({
  title,
  theme,
  idText,
  uid,
  onOpen,
  showSeal = true,
  sealColor,
}: {
  title: string;
  theme?: string | null;
  idText?: string;
  uid: string;
  onOpen?: () => void;
  /** Whether to draw the wax seal. False for the breaking-phase parchment
   *  halves — the seal is then drawn + torn by its own <WaxTear> overlay. */
  showSeal?: boolean;
  /** Portfolio wax-seal base color (0079); null = default bordeaux. */
  sealColor?: string | null;
}) {
  const w = DOSSIER_W;
  const h = DOSSIER_H;
  const s = Math.round(w * 0.34);
  return (
    <div
      style={{
        position: "relative",
        width: w,
        height: h,
        borderRadius: 6,
        border: "1px solid rgba(195,146,53,0.45)",
        boxShadow: onOpen
          ? "var(--gh-shadow-lg, 0 8px 16px rgba(14,42,31,0.14)), var(--gh-glow-sm, 0 0 8px rgba(195,146,53,0.4))"
          : "var(--gh-shadow-lg, 0 8px 16px rgba(14,42,31,0.14))",
        background: "#e7dec4",
      }}
    >
      {/* Left flap (overlaps right; casts the seam shadow). */}
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: "51%",
          zIndex: 2,
          background:
            "linear-gradient(90deg, #d6c9a4 0, #ece4cc 20%, #e7dec4 80%, #ddd0ad 100%)",
          boxShadow: "3px 0 4px rgba(58,47,28,0.28)",
        }}
      />
      {/* Right flap. */}
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 0,
          width: "50%",
          zIndex: 1,
          background:
            "linear-gradient(90deg, #e0d5b6 0, #e9e0c8 35%, #e4dabd 85%, #d0c39c 100%)",
        }}
      />
      {/* Aged-paper wear. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 3,
          pointerEvents: "none",
          borderRadius: 6,
          background:
            "radial-gradient(ellipse at 18% 12%, rgba(122,90,29,0.10), transparent 40%), radial-gradient(ellipse at 85% 78%, rgba(122,90,29,0.12), transparent 45%), radial-gradient(ellipse at 60% 30%, rgba(122,90,29,0.05), transparent 50%)",
          boxShadow: "inset 0 0 24px rgba(58,47,28,0.22)",
        }}
      />
      {/* Portfolio tag (never the creator — contents are secret). Clamped to
          the left flap: left(16px) + maxWidth(40%) keeps the right edge shy of
          the centre fold so it NEVER crosses the seam and breaks the folded-
          parchment illusion. Long titles wrap + clamp rather than overflow. */}
      <div
        style={{
          position: "absolute",
          top: 16,
          left: 16,
          zIndex: 4,
          border: "1px solid rgba(58,47,28,0.4)",
          padding: "4px 8px",
          fontFamily: MONO,
          fontSize: "0.6rem",
          letterSpacing: "0.12em",
          lineHeight: 1.5,
          textTransform: "uppercase",
          color: "#3a2f1c",
          maxWidth: "40%",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 3,
          overflow: "hidden",
          overflowWrap: "break-word",
          wordBreak: "break-word",
          hyphens: "auto",
        }}
      >
        {title}
      </div>
      {/* Bottom-left — the dossier theme (no "sealed": the wax seal says so).
          Clamped to the left flap so it can't cross the seam. */}
      {theme ? (
        <div
          style={{
            position: "absolute",
            bottom: 14,
            left: 16,
            maxWidth: "40%",
            zIndex: 4,
            fontFamily: MONO,
            fontSize: "0.55rem",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "rgba(58,47,28,0.6)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {theme}
        </div>
      ) : null}
      {/* Bottom-right — short dossier id. Clamped to the right flap so its left
          edge stays past the centre fold (the seam rule applies both ways). */}
      {idText ? (
        <div
          style={{
            position: "absolute",
            bottom: 14,
            right: 16,
            maxWidth: "40%",
            zIndex: 4,
            fontFamily: MONO,
            fontSize: "0.58rem",
            letterSpacing: "0.04em",
            color: "rgba(58,47,28,0.7)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {idText}
        </div>
      ) : null}
      {/* Bordeaux wax seal — the tap target. Omitted on the breaking-phase
          halves (the <WaxTear> overlay draws + rips it there instead). */}
      {!showSeal ? null : onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          aria-label="Break the wax seal"
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%,-50%)",
            width: s,
            height: s,
            zIndex: 5,
            border: 0,
            padding: 0,
            background: "transparent",
            cursor: "pointer",
          }}
        >
          <WaxSealArt uid={uid} color={sealColor} />
        </button>
      ) : (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%,-50%)",
            width: s,
            height: s,
            zIndex: 5,
            display: "block",
          }}
        >
          <WaxSealArt uid={uid} color={sealColor} />
        </span>
      )}
    </div>
  );
}

// ── A torn wax half with real thickness (a wax seal is ~2mm, not paper-thin).
//    The seal art is extruded toward the viewer as a short stack of clipped
//    copies: the front layer is the detailed surface (the only one that casts
//    the shadow), the layers behind darken into the wax "body". Melted onto its
//    page, so when the page swings open the stack's side reads as a thick torn
//    wax edge. `left/top` place it (page-local) so the seal centres on the fold. ─
function WaxHalf({
  clip,
  uid,
  left,
  top,
  size,
  thick,
  color,
}: {
  clip: string;
  uid: string;
  left: number;
  top: number;
  size: number;
  thick: number;
  /** Portfolio wax-seal base color (0079); null = default bordeaux. */
  color?: string | null;
}) {
  const N = 6;
  const layers: ReactNode[] = [];
  for (let i = 0; i < N; i++) {
    const f = i / (N - 1); // 0 = deepest body (on the paper) → 1 = front surface
    const isFront = i === N - 1;
    layers.push(
      <div
        key={i}
        style={{
          position: "absolute",
          inset: 0,
          clipPath: clip,
          WebkitClipPath: clip,
          transform: `translateZ(${(2 + f * thick).toFixed(2)}px)`,
          // Body layers darken into the wax interior; the front stays full.
          filter: isFront
            ? undefined
            : `brightness(${(0.55 + 0.4 * f).toFixed(2)})`,
          backfaceVisibility: "hidden",
        }}
      >
        {/* No cast shadow on the halves — the extrusion gives depth, and a
            clipped drop-shadow was what leaked parchment over the seal shadow
            at the tear's end. The intact sealer carries the shadow instead. */}
        <WaxSealArt uid={`${uid}${i}-`} shadow={false} color={color} />
      </div>,
    );
  }
  return (
    <div
      style={{
        position: "absolute",
        left,
        top,
        width: size,
        height: size,
        transformStyle: "preserve-3d",
        backfaceVisibility: "hidden",
        pointerEvents: "none",
      }}
    >
      {layers}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────

export interface RevealCeremonyProps {
  /** Whether the ceremony overlay is visible. */
  open: boolean;
  /** The sealed dossier being opened — drives the gatefold face + eyebrow. */
  dossier: SealedDossierData;
  /** The revealed certificate. `null` until the break triggers the reveal
   *  (first open); provided up front on replay. When present, it rises out
   *  of the opening dossier. */
  share: CertificateShare | null;
  /** The rendered <PatronageCertificate> for `share` (app owns the mapping
   *  so serial formatting stays app-side). `null` while `share` is null. */
  certificate: ReactNode | null;
  /** Display serial for the settled caption ("serial GH-XXXX-XXXX"). */
  serial?: string;
  /** Dossier theme printed on the sealed face (bottom-left), e.g.
   *  "Counter-Strike". Omitted → nothing shown there. */
  theme?: string | null;
  /** Short opaque dossier id printed on the sealed face (bottom-right). Kept
   *  short by the caller so it can't cross the gatefold seam. Falls back to a
   *  slice of `dossier.id`. */
  dossierId?: string;
  /** The dossier's creator roster — floats by (marquee) on the approach
   *  screen, as on the shop hero. WHICH creator is inside stays secret; this
   *  is the whole possible roster, exactly like the shop card. */
  creators?: Array<{ name: string; img: string | null }>;
  /** Collapse the whole sequence to ~0.6s (OS/user reduced-motion). */
  reducedMotion?: boolean;
  /** Story 3.7 / 6.6 — reduced mobile-fidelity tier. Sheds the costliest
   *  burst FX (glow blobs, blurred shockwave ring, most sparks) so weak
   *  or frame-dropping devices keep the ceremony smooth. Distinct from
   *  `reducedMotion`: the full timed sequence still plays, it just spends
   *  less GPU per frame. Fed by `useEffectiveMobileFidelity()` (phone
   *  viewport OR the session auto-degrade guard). */
  mobileFidelity?: boolean;
  /** Fires on "Break the seal" — the caller triggers the reveal mutation
   *  (first open). Safe to omit / no-op on replay (share already set). */
  onBreak?: () => void;
  /** Settled "View certificate" — when provided, the button shows. */
  onViewCertificate?: (share: CertificateShare) => void;
  /** Dismiss (X / "← Collection" / "Back to collection" / ESC when settled). */
  onClose: () => void;
  /** Offer the settled "Watch again" replay affordance. Default true. */
  allowReplay?: boolean;
}

export const RevealCeremony: FC<RevealCeremonyProps> = ({
  open,
  dossier,
  share,
  certificate,
  serial,
  theme,
  dossierId,
  creators,
  reducedMotion = false,
  mobileFidelity = false,
  onBreak,
  onViewCertificate,
  onClose,
  allowReplay = true,
}) => {
  const [phase, setPhase] = useState<Phase>("approach");
  const [fxSeed, setFxSeed] = useState(11);
  // The 400×480 stage grows to fill the space the screen allows (desktop-only)
  // — the ceremony is the one surface that spends the whole motion budget, so
  // it should own the screen when there's room.
  const [stageScale, setStageScale] = useState(1);
  // Set when the user breaks before `share` has resolved (first open) — the
  // sequence starts as soon as `share` arrives so timing is tier-accurate.
  const pendingBreak = useRef(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Signature pen — measured against the live signature box once settled.
  const certWrapRef = useRef<HTMLDivElement | null>(null);
  const [penCss, setPenCss] = useState<string | null>(null);
  const [penKey, setPenKey] = useState(0);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  // Reset when the overlay (re)opens.
  useEffect(() => {
    if (open) {
      setPhase("approach");
      pendingBreak.current = false;
    }
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Lock the background page scroll while the ceremony is open — it's a fixed
  // fullscreen modal, so the (long) Collection page behind it must not scroll.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const tier: CertificateTier = share?.tier ?? "premium";
  const T = reducedMotion ? REDUCED_T : TIER_T[tier];
  // Beat boundaries as fractions of the total. The tear is the longest beat
  // (slow, suspenseful); the tier burst is a short flash; the gatefold open is
  // suspense again; the cert rise is the "who" reveal.
  const BEAT_BURST = 0.36; // tear  runs 0    → 0.36T (slow)
  const BEAT_OPEN = 0.56; //  burst runs 0.36 → 0.56T (flash blooms + SETTLES)
  const BEAT_HOLD = 0.76; //  open  runs 0.56 → 0.76T (slow, suspenseful)
  const BEAT_RISE = 0.84; //  hold  runs 0.76 → 0.84T (brief pause, cert blurred)
  //                          rise  runs 0.84 → 1.0T  (cert zooms in + sharpens)
  const tearDur = Math.round(T * BEAT_BURST);
  const openDur = Math.round(T * (BEAT_HOLD - BEAT_OPEN));
  // The flash blooms then settles WITHIN the burst beat, so the tier colour
  // lands in the mind before the pages start to open.
  const burstDur = Math.round(T * (BEAT_OPEN - BEAT_BURST));
  // Cert animation spans opening → settled: it holds SMALL + BLURRED behind the
  // opening pages and through the fully-open pause, then zooms to full + sharpens.
  const riseDur = Math.round(T * (1 - BEAT_OPEN));

  const startBreaking = (revealedTier: CertificateTier) => {
    const total = reducedMotion ? REDUCED_T : TIER_T[revealedTier];
    setFxSeed(1 + Math.floor(Math.random() * 233279));
    setPhase("tearing");
    clearTimers();
    timers.current = [
      setTimeout(() => setPhase("burst"), Math.round(total * BEAT_BURST)),
      setTimeout(() => setPhase("opening"), Math.round(total * BEAT_OPEN)),
      setTimeout(() => setPhase("hold"), Math.round(total * BEAT_HOLD)),
      setTimeout(() => setPhase("rising"), Math.round(total * BEAT_RISE)),
      setTimeout(() => setPhase("settled"), total),
    ];
  };

  // First-open: the break was requested before `share` resolved — start as
  // soon as it arrives (tier now known).
  useEffect(() => {
    if (pendingBreak.current && share && phase === "approach") {
      pendingBreak.current = false;
      startBreaking(share.tier);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [share, phase]);

  const handleBreak = () => {
    if (phase !== "approach") return;
    onBreak?.();
    if (share) {
      startBreaking(share.tier);
    } else {
      // Wait for the reveal to resolve (the effect above starts it).
      pendingBreak.current = true;
    }
  };

  const handleWatchAgain = () => {
    clearTimers();
    pendingBreak.current = false;
    setPhase("approach");
  };

  const handleClose = () => {
    clearTimers();
    onClose();
  };

  // ESC — allowed at approach + settled only (no mid-ceremony skip).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && (phase === "approach" || phase === "settled")) {
        handleClose();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase]);

  // Signature pen: once settled on a SIGNED cert (and motion allowed), measure
  // the live signature box and generate the pen's fly-in/scribble/fly-out
  // keyframes from it. Two rAFs so the cert's rise animation has settled to
  // full size before we read its rect.
  useEffect(() => {
    if (phase !== "settled" || !share?.signed || reducedMotion) {
      setPenCss(null);
      return;
    }
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const sig = certWrapRef.current?.querySelector(
          ".gh-cert-signature",
        ) as HTMLElement | null;
        if (!sig) return;
        const r = sig.getBoundingClientRect();
        if (r.width < 2 || r.height < 2) return;
        setPenCss(buildPenScribble(r, window.innerWidth));
        setPenKey((k) => k + 1);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, share, reducedMotion]);

  // Fit-to-screen: scale the stage up to fill the room left after the header +
  // caption/actions. Grows to ~1.75× on tall screens; never shrinks below 1.
  useEffect(() => {
    if (!open) return;
    function compute() {
      // Reserve for the chrome around the stage: header (~64) + caption slot
      // (150) + gap (8) + main bottom padding (64) + a safety margin so
      // sub-pixel rounding can't tip it into a scrollbar.
      const availH = window.innerHeight - 320;
      const availW = window.innerWidth - 64;
      const k = Math.min(availH / 480, availW / 400);
      setStageScale(Math.max(1, Math.min(1.75, k)));
    }
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [open]);

  if (!open) return null;

  const tierLight = TIER_LIGHT[tier];
  const title = dossier.portfolio_title;
  const idText =
    dossierId ?? `#${(dossier.id || "").slice(0, 8).toUpperCase()}`;
  // Portfolio wax-seal color (0079) — drives every seal mount in the
  // ceremony (face, torn halves, tear overlay) plus the crack ink.
  const sealColor = dossier.seal_color ?? null;
  const waxTearInk = deriveWaxPalette(sealColor).tearInk;
  // The wax seal box — the SAME size as DossierFace's sealed seal (no scaling)
  // so it reads identically the instant the sealed face swaps to the tear. The
  // clips leave the art's melted-rim overspill intact (they extend past the
  // box). ONE fixed tear shape (V1).
  const sealBox = Math.round(DOSSIER_W * 0.34);
  // Wax thickness (~2mm on a real seal): ~12% of the seal's width, extruded
  // toward the viewer so the torn edge has depth when the pages open.
  const WAX_THICK = Math.max(4, Math.round(sealBox * 0.12));
  const waxTearPts = waxTearLine(sealBox);
  const waxClip = waxClips(waxTearPts, sealBox);
  const eyebrowStyle = {
    fontFamily: MONO,
    fontSize: "0.68rem",
    textTransform: "uppercase" as const,
    letterSpacing: "0.12em",
    color: "#dcb957",
  };

  // Beat-driven visibility. The gatefold+wax sit on stage from the tear through
  // the rise (fading as the cert takes over); pages hold shut until `opening`.
  const active =
    phase === "tearing" ||
    phase === "burst" ||
    phase === "opening" ||
    phase === "hold" ||
    phase === "rising";
  const pagesOpen =
    phase === "opening" || phase === "hold" || phase === "rising";
  const showBurst = phase === "burst" || phase === "opening";
  const showCert =
    phase === "opening" ||
    phase === "hold" ||
    phase === "rising" ||
    phase === "settled";
  const dossierFaded = phase === "rising" || phase === "settled";
  const certZ = phase === "opening" || phase === "hold" ? 1 : 3;
  // Signature inks in only once settled, and only if the cert rolled signed.
  // Reduced-motion shows it instantly (no wipe).
  const signPhase = share?.signed
    ? phase === "settled"
      ? reducedMotion
        ? "gh-cert-sig-shown"
        : "gh-cert-sig-draw"
      : "gh-cert-sig-hidden"
    : undefined;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Reveal ceremony"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background:
          "radial-gradient(ellipse at 50% 42%, #134d32 0%, #0e2a1f 55%, #0a2018 100%)",
        color: "#faf8f2",
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        display: "grid",
        gridTemplateRows: "auto 1fr",
        // A fixed fullscreen surface — it must never scroll. The stage scales to
        // fit (see the fit-to-screen effect); anything past the edges is clipped
        // rather than producing a scrollbar.
        overflow: "hidden",
      }}
    >
      <style>{KEYFRAMES}</style>

      {/* aria-live — announces the outcome for screen readers. */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {phase === "settled" && share
          ? `Revealed: ${TIER_LABEL[tier]} certificate of ${share.snapshot_display_name}.`
          : ""}
      </div>

      <div style={{ padding: "20px 40px" }}>
        <button
          type="button"
          onClick={handleClose}
          disabled={active}
          style={{
            fontSize: "0.8rem",
            background: "transparent",
            border: 0,
            color: "#dcb957",
            opacity: active ? 0.35 : 0.8,
            cursor: active ? "default" : "pointer",
            padding: 0,
          }}
        >
          ← Collection
        </button>
      </div>

      <main
        style={{
          display: "grid",
          placeItems: "center",
          padding: "0 24px 64px",
        }}
      >
        <div style={{ display: "grid", justifyItems: "center", gap: 8 }}>
          {/* Reserve the scaled box so the caption flows below the (bigger)
              stage; the inner element scales the whole 400×480 stage as one. */}
          <div style={{ width: 400 * stageScale, height: 480 * stageScale }}>
          <div
            style={{
              position: "relative",
              width: 400,
              height: 480,
              transform: `scale(${stageScale})`,
              transformOrigin: "top left",
            }}
          >
            {/* Static back panel — the inside of the dossier, seen once the
                pages part; fades out as the certificate takes over. */}
            {pagesOpen && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 0,
                  display: "grid",
                  placeItems: "center",
                  opacity: dossierFaded ? 0 : 1,
                  transition: "opacity 450ms ease",
                  pointerEvents: "none",
                }}
              >
                <div
                  style={{
                    width: DOSSIER_W,
                    height: DOSSIER_H,
                    borderRadius: 6,
                    border: "1px solid rgba(122,90,29,0.4)",
                    background:
                      "linear-gradient(120deg, #cbbc94 0, #dccfab 45%, #c4b48a 100%)",
                    boxShadow:
                      "inset 0 0 34px rgba(58,47,28,0.38), var(--gh-shadow-lg, 0 8px 16px rgba(14,42,31,0.14))",
                  }}
                />
              </div>
            )}

            {/* Certificate — glimpsed small behind the parting pages (opening),
                then rises into full view (the "who" reveal). Its signature inks
                in only at `settled` (the gh-cert-sig-* class drives it). */}
            {showCert && (
              <div
                ref={certWrapRef}
                className={signPhase}
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  placeItems: "center",
                  zIndex: certZ,
                  animation: `gh-cert-rise ${riseDur}ms ${EASE} both`,
                }}
              >
                {certificate}
              </div>
            )}

            {/* Dossier — approach face, or the 3D-splitting gatefold. Fades as
                the cert rises (rising), gone at settled. */}
            {phase !== "settled" && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  display: "grid",
                  placeItems: "center",
                  animation: "gh-float 3.2s ease-in-out infinite",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    width: DOSSIER_W,
                    height: DOSSIER_H,
                    perspective: "1100px",
                    opacity: dossierFaded ? 0 : 1,
                    transition: "opacity 450ms ease",
                  }}
                >
                  {phase === "approach" ? (
                    <div style={{ position: "absolute", inset: 0, zIndex: 2 }}>
                      <DossierFace
                        title={title}
                        theme={theme}
                        idText={idText}
                        uid="gh-rc-ap-"
                        onOpen={handleBreak}
                        sealColor={sealColor}
                      />
                    </div>
                  ) : (
                    <>
                      {/* THE TIER REVEAL — tier-coloured light bursts from the
                          torn seam the instant the wax gives way, before the
                          pages open. */}
                      {showBurst && (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            // BEHIND the left flap (z5), in FRONT of the right
                            // flap (z3): a real dossier's left flap overlaps the
                            // right, so the flash is masked on the left by the
                            // left flap and only shows on the right — it reads as
                            // light flowing OUT of the seam toward the right,
                            // not sitting on top of the left side.
                            zIndex: 4,
                            // Clipped to the dossier silhouette so NO light flashes
                            // out past its edges (esp. the left edge).
                            overflow: "hidden",
                            borderRadius: 6,
                            pointerEvents: "none",
                          }}
                        >
                          {burstFX(
                            tierLight,
                            DOSSIER_W,
                            burstDur,
                            fxSeed,
                            true,
                            mobileFidelity,
                          )}
                        </div>
                      )}

                      {/* Right parchment page — straight fold, hinged right.
                          Holds shut while the wax tears (delay), then opens. The
                          seal is drawn by the <WaxTear> overlay, not here. */}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          width: "50%",
                          height: "100%",
                          zIndex: 3,
                          transformOrigin: "right center",
                          transformStyle: "preserve-3d",
                          animation: pagesOpen
                            ? `gh-half-r ${openDur}ms ${EASE} forwards`
                            : "none",
                          pointerEvents: "none",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            overflow: "hidden",
                            borderRadius: "0 6px 6px 0",
                            backfaceVisibility: "hidden",
                            transform: "translateZ(1px)",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              left: -Math.round(DOSSIER_W / 2),
                              top: 0,
                            }}
                          >
                            <DossierFace
                              title={title}
                              theme={theme}
                              idText={idText}
                              uid="gh-rc-hr-"
                              showSeal={false}
                            />
                          </div>
                        </div>
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            backfaceVisibility: "hidden",
                            transform: "rotateY(180deg) translateZ(1px)",
                            borderRadius: "0 6px 6px 0",
                            border: "1px solid rgba(122,90,29,0.35)",
                            background:
                              "linear-gradient(90deg, #dccfab 0, #d6c9a4 55%, #cbbc94 100%)",
                            boxShadow: "inset 0 0 24px rgba(58,47,28,0.28)",
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: 1,
                            bottom: 1,
                            left: -1,
                            width: 2,
                            borderRadius: 1,
                            transform: "rotateY(90deg)",
                            background: "linear-gradient(#cfc09a, #b8a87e)",
                            pointerEvents: "none",
                          }}
                        />
                        {/* Right wax half — melted onto THIS page (page-local
                            x=0 is the fold), clipped to the right of the jagged
                            tear. Extruded for real thickness; rides the page
                            open and never leaves the paper. Only mounted once
                            the pages open — during the flat tear beat the seal
                            is drawn by the self-contained overlay below. */}
                        {pagesOpen && (
                          <WaxHalf
                            clip={waxClip.right}
                            uid="gh-rc-wr-"
                            left={-Math.round(sealBox / 2)}
                            top={Math.round(DOSSIER_H / 2 - sealBox / 2)}
                            size={sealBox}
                            thick={WAX_THICK}
                            color={sealColor}
                          />
                        )}
                      </div>

                      {/* Left parchment page — straight fold, hinged left. */}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "50%",
                          height: "100%",
                          zIndex: 5,
                          transformOrigin: "left center",
                          transformStyle: "preserve-3d",
                          animation: pagesOpen
                            ? `gh-half-l ${openDur}ms ${EASE} forwards`
                            : "none",
                          pointerEvents: "none",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            overflow: "hidden",
                            borderRadius: "6px 0 0 6px",
                            backfaceVisibility: "hidden",
                            transform: "translateZ(1px)",
                          }}
                        >
                          <div style={{ position: "absolute", left: 0, top: 0 }}>
                            <DossierFace
                              title={title}
                              theme={theme}
                              idText={idText}
                              uid="gh-rc-hl-"
                              showSeal={false}
                            />
                          </div>
                        </div>
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            backfaceVisibility: "hidden",
                            transform: "rotateY(180deg) translateZ(1px)",
                            borderRadius: "6px 0 0 6px",
                            border: "1px solid rgba(122,90,29,0.35)",
                            background:
                              "linear-gradient(90deg, #cbbc94 0, #d6c9a4 45%, #dccfab 100%)",
                            boxShadow: "inset 0 0 24px rgba(58,47,28,0.28)",
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: 1,
                            bottom: 1,
                            right: -1,
                            width: 2,
                            borderRadius: 1,
                            transform: "rotateY(90deg)",
                            background: "linear-gradient(#cfc09a, #b8a87e)",
                            pointerEvents: "none",
                          }}
                        />
                        {/* Left wax half — melted onto THIS page, clipped to the
                            left of the jagged tear; extruded for thickness. Only
                            mounted once the pages open (see right half). */}
                        {pagesOpen && (
                          <WaxHalf
                            clip={waxClip.left}
                            uid="gh-rc-wl-"
                            left={Math.round(DOSSIER_W / 2 - sealBox / 2)}
                            top={Math.round(DOSSIER_H / 2 - sealBox / 2)}
                            size={sealBox}
                            thick={WAX_THICK}
                            color={sealColor}
                          />
                        )}
                      </div>

                      {/* Self-contained seal-tear overlay — flat, centred, while
                          the pages hold shut (tear + burst beats). It stacks, back
                          to front:
                            1. a STATIC full-seal silhouette that carries the ONE
                               cast shadow (never clipped → the shadow doesn't
                               travel with the tear, doesn't double at the seam);
                            2. the two torn halves (shadow-less) that tile the full
                               seal and reveal the jagged crack between them;
                            3. the "intact" sealer, retracting top→bottom to
                               choreograph the slow tear (tear beat only);
                            4. the wax-coloured light tracing the tear front.
                          Once the pages open, the per-page WaxHalves take over
                          (they match this exactly at rotation 0, so the swap is
                          seamless) and this overlay unmounts. */}
                      {(phase === "tearing" || phase === "burst") && (
                        <div
                          style={{
                            position: "absolute",
                            left: Math.round(DOSSIER_W / 2 - sealBox / 2),
                            top: Math.round(DOSSIER_H / 2 - sealBox / 2),
                            width: sealBox,
                            height: sealBox,
                            zIndex: 7,
                            pointerEvents: "none",
                          }}
                        >
                          {/* (1) static shadow silhouette — covered by the halves,
                              so only its drop-shadow rim shows. */}
                          <div style={{ position: "absolute", inset: 0 }}>
                            <WaxSealArt
                              uid="gh-rc-wsh-"
                              shadow={true}
                              color={sealColor}
                            />
                          </div>
                          {/* (2) torn halves, flat. */}
                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              clipPath: waxClip.left,
                              WebkitClipPath: waxClip.left,
                            }}
                          >
                            <WaxSealArt
                              uid="gh-rc-fl-"
                              shadow={false}
                              color={sealColor}
                            />
                          </div>
                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              clipPath: waxClip.right,
                              WebkitClipPath: waxClip.right,
                            }}
                          >
                            <WaxSealArt
                              uid="gh-rc-fr-"
                              shadow={false}
                              color={sealColor}
                            />
                          </div>
                          {/* (3) intact sealer, retracting — tear beat only. No
                              shadow (the static silhouette carries it), so the
                              clipped edge casts no travelling shadow line. */}
                          {phase === "tearing" && (
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                animation: `gh-wax-unzip ${tearDur}ms ${EASE} forwards`,
                              }}
                            >
                              <WaxSealArt
                                uid="gh-rc-ws-"
                                shadow={false}
                                color={sealColor}
                              />
                            </div>
                          )}
                          {/* (4) the crack — revealed during the tear, then
                              HELD through the burst (see gh-wax-tearlight) so
                              the wax doesn't heal before the pages open. */}
                          {waxTearLight(
                            waxTearPts,
                            waxTearInk,
                            sealBox,
                            tearDur,
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          </div>

          {/* Caption + actions. */}
          <div
            style={{
              minHeight: 150,
              display: "grid",
              justifyItems: "center",
              gap: 16,
              textAlign: "center",
            }}
          >
            {phase === "approach" && (
              <div
                style={{
                  display: "grid",
                  justifyItems: "center",
                  gap: 16,
                  animation: `gh-fadeup 400ms ${EASE} both`,
                }}
              >
                <div style={eyebrowStyle}>{title}</div>
                <Button variant="primary" size="lg" onClick={handleBreak}>
                  Break the seal
                </Button>
                {/* The creators inside float by (whole roster; WHICH one is
                    yours stays secret) — the shop-hero marquee, restaged. */}
                {creators && creators.length > 0 && (
                  <div
                    style={{
                      overflow: "hidden",
                      maxWidth: 340,
                      WebkitMaskImage:
                        "linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent)",
                      maskImage:
                        "linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        width: "max-content",
                        animation: `gh-rc-marquee ${Math.max(
                          16,
                          creators.length * 4,
                        )}s linear infinite`,
                      }}
                    >
                      {[...creators, ...creators].map((c, i) => (
                        <div
                          key={i}
                          style={{
                            display: "grid",
                            justifyItems: "center",
                            gap: 6,
                            marginRight: 22,
                          }}
                        >
                          {c.img ? (
                            <img
                              src={c.img}
                              alt=""
                              style={{
                                width: 44,
                                height: 44,
                                borderRadius: "50%",
                                objectFit: "cover",
                                border: "1px solid rgba(220,185,87,0.4)",
                              }}
                            />
                          ) : (
                            <span
                              style={{
                                width: 44,
                                height: 44,
                                borderRadius: "50%",
                                display: "grid",
                                placeItems: "center",
                                background: "rgba(220,185,87,0.18)",
                                color: "#dcb957",
                                fontFamily: MONO,
                                fontSize: "0.9rem",
                              }}
                            >
                              {(c.name || "?")
                                .replace(/^@/, "")
                                .charAt(0)
                                .toUpperCase() || "?"}
                            </span>
                          )}
                          <span
                            style={{
                              fontFamily: MONO,
                              fontSize: "0.5rem",
                              letterSpacing: "0.08em",
                              textTransform: "uppercase",
                              color: "rgba(250,248,242,0.55)",
                              maxWidth: 64,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {c.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {phase === "settled" && (
              <div
                style={{
                  display: "grid",
                  justifyItems: "center",
                  gap: 16,
                  animation: `gh-fadeup 500ms ${EASE} both`,
                }}
              >
                <div style={eyebrowStyle}>
                  Revealed · {TIER_LABEL[tier]}
                  {serial ? ` · serial ${serial}` : ""}
                </div>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  {onViewCertificate && share && (
                    <Button
                      variant="primary"
                      size="md"
                      onClick={() => onViewCertificate(share)}
                    >
                      View certificate
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="md"
                    className="!text-brand-paper-100 hover:!bg-white/10"
                    onClick={handleClose}
                  >
                    Back to collection
                  </Button>
                </div>
                {allowReplay && (
                  <button
                    type="button"
                    onClick={handleWatchAgain}
                    style={{
                      fontSize: "0.75rem",
                      opacity: 0.6,
                      background: "transparent",
                      border: 0,
                      color: "#faf8f2",
                      cursor: "pointer",
                    }}
                  >
                    Watch again
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Signature pen — flies in from screen-right, scribbles along the ink
          wipe, flies back out. Positioned in viewport coords (this overlay is
          fixed inset:0). Remounts via penKey so "Watch again" replays it. */}
      {phase === "settled" && penCss && (
        <>
          <style>{penCss}</style>
          {/* Viewport-clipped so the pen flying off-screen right can't spawn a
              scrollbar. */}
          <div
            style={{
              position: "fixed",
              inset: 0,
              overflow: "hidden",
              zIndex: 60,
              pointerEvents: "none",
            }}
          >
            <div
              key={penKey}
              aria-hidden="true"
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                pointerEvents: "none",
                animation: `gh-pen-scribble ${PEN_TOTAL}ms linear both`,
                willChange: "transform, opacity",
              }}
            >
              <PenSprite />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RevealCeremony;
